import {
  alignmentsAtom,
  alignmentWalkAtom,
  DEFAULT_WALK_CLIP,
  selectedAlignmentKeyAtom,
  type AlignmentWalkState,
} from '@/atoms/alignment.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { stationDockOpenAtom } from '@/atoms/stationVolumes.atom';
import {
  addLineModeAtom,
  addPointModeAtom,
  addPolygonModeAtom,
  addSurfaceModeAtom,
  clipBoxStateAtom,
  viewerEvents,
  type ClipBoxInteractionMode,
  type ClipBoxMode,
} from '@/atoms/viewer.atom';
import {
  clipBoxAtStation,
  degradationList,
  displayToInternal,
  formatStation,
  gradeAtStation,
  internalToDisplay,
  nextKeyPoint,
  parseStation,
  pointAtStation,
  type Alignment,
} from '@/core/alignment';
import { cn } from '@/lib/utils';
import { useHotkeys } from '@tanstack/react-hotkeys';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { FloatingToolbar } from './FloatingToolbar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { NumericField } from './ui/numeric-field';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';

/**
 * "2 element(s) drawn as straight chords (spiral at STA 0+100.00, …)" — the
 * one-line form of an alignment's chord fallbacks, for a `title`.
 */
function degradedWarning(a: Alignment, t: (key: string, vars?: Record<string, unknown>) => string): string {
  return t('alignments.degraded.warning', { count: a.degradations.length, list: degradationList(a, t) });
}

const STEP_PRESETS = [1, 5, 10, 20, 50, 100];

/**
 * Pure step logic — exported for tests.
 *
 * In snap mode we walk the alignment's key points; when there is no further
 * key point in `dir` we land on the end (forward) / start (backward) so the
 * button never becomes a silent no-op. Otherwise we advance by `step * mult`
 * metres, clamped to `[0, length]`.
 */
export function stepStation(a: Alignment, walk: AlignmentWalkState, dir: 1 | -1, mult = 1): number {
  if (walk.snapKeyPoints) {
    const next = nextKeyPoint(a, walk.station, dir);
    return next ?? (dir === 1 ? a.length : 0);
  }
  return Math.min(a.length, Math.max(0, walk.station + dir * walk.step * mult));
}

/** Clip-box dimensions read to 2 dp, trailing zeros trimmed: `20`, `2.35`. */
const clipDim = (v: number) => String(Math.round(v * 100) / 100);

/**
 * True while the user is picking vertices for a new point/line/polygon/
 * surface. Shared by the toolbar (which stands down for the duration) and by
 * `WalkToolbarWindow`, so the movable window and the bar inside it appear and
 * disappear together.
 */
export const useAnyCreationToolActive = (): boolean => {
  const addPointMode = useAtomValue(addPointModeAtom);
  const addLineMode = useAtomValue(addLineModeAtom);
  const addPolygonMode = useAtomValue(addPolygonModeAtom);
  const addSurfaceMode = useAtomValue(addSurfaceModeAtom);
  return addPointMode.enabled || addLineMode.enabled || addPolygonMode.enabled || addSurfaceMode.enabled;
};

/**
 * Whether `AlignmentWalkToolbar` renders anything at all. The bar itself calls
 * this for its own `return null`, and `WalkToolbarWindow` (spec §12.2) mounts
 * on it, so an empty window with a bare grip can never sit on the canvas while
 * no walk is running — one predicate, in one place.
 */
export const useWalkToolbarVisible = (): boolean => {
  const walk = useAtomValue(alignmentWalkAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const anyCreationToolActive = useAnyCreationToolActive();
  return !!walk && !!alignments.get(walk.key) && !anyCreationToolActive;
};

/**
 * Top-centre floating toolbar (shared tool slot) for "walking" a road alignment: step the
 * current station, type one, scrub it with a slider, snap to key points and
 * optionally drive the camera target along the way.
 *
 * Renders nothing unless `alignmentWalkAtom` points at a registered alignment
 * (`useWalkToolbarVisible`, which `WalkToolbarWindow` mounts on too).
 */
export const AlignmentWalkToolbar = ({
  projectId,
  clipBoxGate,
}: {
  projectId: string;
  /** `viewer.clipBox` feature gate from the Viewer; omitted = allowed. */
  clipBoxGate?: { allowed: boolean; promptUpgrade: () => void };
}) => {
  const { t } = useTranslation('components');
  // Degradation copy lives with the other station strings in `project`.
  const { t: tp } = useTranslation('project');
  const store = useStore();
  const [clipBox, setClipBox] = useAtom(clipBoxStateAtom(projectId));
  const alignments = useAtomValue(alignmentsAtom);
  const layers = useAtomValue(layersAtom);
  const stationDockOpen = useAtomValue(stationDockOpenAtom);
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const setSelectedKey = useSetAtom(selectedAlignmentKeyAtom);
  const alignment = walk ? alignments.get(walk.key) : undefined;

  // The bar's own visibility rule, and the one `WalkToolbarWindow` mounts on
  // (M12): while the user is picking vertices for a new point/line/polygon/
  // surface this toolbar's hotkeys (Escape, brackets, Home/End) would fight
  // the creation tool's own bindings, and both toolbars share the top-centre
  // slot — so the bar stands down for the duration, window and all.
  const visible = useWalkToolbarVisible();

  // Alignments grouped by their owning layer for the picker below.
  const alignmentGroups = useMemo(() => {
    const layerNames = new Map(layers.map((l) => [l.id, l.name]));
    const byLayer = new Map<string, { layerId: string; label: string; items: Alignment[] }>();
    for (const a of alignments.values()) {
      const g =
        byLayer.get(a.layerId) ??
        byLayer
          .set(a.layerId, { layerId: a.layerId, label: layerNames.get(a.layerId) ?? a.layerId, items: [] })
          .get(a.layerId)!;
      g.items.push(a);
    }
    return [...byLayer.values()];
  }, [alignments, layers]);

  // The station text field keeps its own buffer so partial typing ("1+", "-")
  // doesn't fight the atom; `editing` suppresses the sync-back while focused.
  const [stationText, setStationText] = useState('');
  const [editing, setEditing] = useState(false);
  // Set by the Escape branch so the blur it triggers doesn't commit the buffer.
  // Only ever touched inside event handlers — never read during render.
  const cancelRef = useRef(false);
  const displayStation = alignment && walk ? internalToDisplay(alignment, walk.station) : 0;
  useEffect(() => {
    if (!editing) setStationText(formatStation(displayStation));
  }, [displayStation, editing]);

  // "Custom…" is a UI mode, not a value: `walk.step` can legitimately equal a
  // preset while the user is typing a custom one, so the mode needs its own
  // flag or the NumericField would unmount mid-edit.
  const [customStepMode, setCustomStepMode] = useState(false);
  // Draft mirror of what the user typed into the custom-step field. Passing it
  // straight back as NumericField's `value` keeps its internal resync effect
  // quiet while the string is in a transient state ("0", "0."), which would
  // otherwise clobber the buffer back to the stored step.
  const [stepDraft, setStepDraft] = useState<number | null>(null);

  // Camera follow — coalesced to at most one emit per animation frame so
  // dragging the slider doesn't fire a setTarget per pointermove. Depends on
  // the individual fields rather than the `walk` object: the atom is replaced
  // on every change, so `[walk]` would re-emit when only the step preset or a
  // snap toggle moved.
  const walkStation = walk?.station;
  const walkFollow = walk?.follow;
  const walkKey = walk?.key;
  useEffect(() => {
    if (!walkFollow || !walkKey || walkStation === undefined) return;
    const id = requestAnimationFrame(() => {
      // Resolve the alignment at EMIT time rather than depending on the object:
      // any producer re-registering (a feature refetch, a layer-settings tweak)
      // hands out a brand-new Alignment instance with identical geometry, and a
      // dependency on it would snap the camera back to `walkStation` every time
      // the user had panned away.
      const a = store.get(alignmentsAtom).get(walkKey);
      if (!a) return;
      const pt = pointAtStation(a, walkStation);
      viewerEvents.emit('setCenterTarget', { x: pt.x, y: pt.y, z: pt.z });
    });
    return () => cancelAnimationFrame(id);
    // `store` is stable for the lifetime of the Provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkStation, walkFollow, walkKey]);

  // Clip-box follow — same shape as the camera follow above: one atom write
  // per frame, alignment resolved at write time. Writes center / yaw / size
  // straight into the shared clip-box state so ClipBoxTool renders the slice
  // and the point-cloud materials pick it up on their next frame.
  const walkClip = walk?.clip ?? null;
  const clipLength = walkClip?.length;
  const clipWidth = walkClip?.width;
  const clipHeight = walkClip?.height;
  // §11.1 "Clip to station": asymmetric swaths shift the box sideways, and the
  // slab is centred on the elevations the run measured rather than on the
  // alignment's own z (ruling D35) — a flat alignment has no useful z.
  const clipLateralOffset = walkClip?.lateralOffset;
  const clipVerticalCentre = walkClip?.verticalCentre;
  useEffect(() => {
    if (!walkKey || walkStation === undefined || clipLength === undefined) return;
    const id = requestAnimationFrame(() => {
      const a = store.get(alignmentsAtom).get(walkKey);
      if (!a) return;
      const box = clipBoxAtStation(a, walkStation, {
        length: clipLength,
        width: clipWidth!,
        height: clipHeight!,
        lateralOffset: clipLateralOffset,
        verticalCentre: clipVerticalCentre,
      });
      setClipBox((prev) => ({ ...prev, ...box, enabled: true, interactionMode: 'VIEW', clipMode: 'CLIP_OUTSIDE' }));
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkStation, walkKey, clipLength, clipWidth, clipHeight, clipLateralOffset, clipVerticalCentre]);

  // What to put back when the follow ends. `interactionMode` is forced to VIEW
  // while following (the gizmo would fight the per-step writes) and `clipMode`
  // to CLIP_OUTSIDE (a walk along an alignment is a request to SEE the slice,
  // not to tint it — the atom's default is HIGHLIGHT_INSIDE); `enabled` is
  // only reverted on close, and only if the walk was what turned the box on.
  const [clipRestore, setClipRestore] = useState<{
    enabled: boolean;
    interactionMode: ClipBoxInteractionMode;
    clipMode: ClipBoxMode;
  } | null>(null);
  const clipEnabled = clipBox.enabled;
  const clipInteraction = clipBox.interactionMode;
  const clipMode = clipBox.clipMode;
  const setClipFollow = (on: boolean) => {
    if (!walk) return;
    if (on) {
      if (clipBoxGate && !clipBoxGate.allowed) {
        clipBoxGate.promptUpgrade();
        return;
      }
      if (!alignment) return;
      const dims = walk.clip ?? DEFAULT_WALK_CLIP;
      setClipRestore({ enabled: clipEnabled, interactionMode: clipInteraction, clipMode });
      setWalk({ ...walk, clip: dims });
      // Enable synchronously (same batch as the walk update) rather than in the
      // rAF effect below — otherwise the "switched off elsewhere" watcher sees
      // clip on + box off for one render and cancels the follow immediately.
      const box = clipBoxAtStation(alignment, walk.station, dims);
      setClipBox((prev) => ({ ...prev, ...box, enabled: true, interactionMode: 'VIEW', clipMode: 'CLIP_OUTSIDE' }));
    } else {
      setWalk({ ...walk, clip: null });
      if (clipRestore)
        setClipBox((prev) => ({
          ...prev,
          interactionMode: clipRestore.interactionMode,
          clipMode: clipRestore.clipMode,
        }));
      setClipRestore(null);
    }
  };
  // The user switched the box off elsewhere (toolbar button / hotkey): stop
  // following instead of re-enabling it on the next step.
  useEffect(() => {
    if (walkClip && !clipEnabled) setWalk((w) => (w ? { ...w, clip: null } : w));
  }, [walkClip, clipEnabled, setWalk]);
  // Walk ended (✕ / Escape / the alignment vanished) while a follow had been
  // started: put the box back the way the walk found it. Only `clipRestore`
  // says a follow was ever on — `walk.clip` is gone with the walk.
  useEffect(() => {
    if (walk || !clipRestore) return;
    setClipBox((prev) => ({
      ...prev,
      interactionMode: clipRestore.interactionMode,
      clipMode: clipRestore.clipMode,
      enabled: clipRestore.enabled,
    }));
    setClipRestore(null);
  }, [walk, clipRestore, setClipBox]);

  // The toolbar stays mounted for the whole viewer session, so the custom-step
  // UI state below outlives a walk session. Clear it whenever the session
  // changes (ending a walk, starting one, switching alignment) — otherwise a
  // fresh session, whose step is reset to DEFAULT_WALK_STEP, would still show
  // "Custom…" plus the previous session's draft value.
  useEffect(() => {
    setCustomStepMode(false);
    setStepDraft(null);
  }, [walkKey]);

  const setStation = (station: number) => setWalk((w) => (w ? { ...w, station } : w));
  const step = (dir: 1 | -1, mult = 1) => {
    if (!alignment || !walk) return;
    setStation(stepStation(alignment, walk, dir, mult));
  };
  const close = () => {
    setWalk(null);
    setSelectedKey(null);
  };
  const commitText = () => {
    // Escape already reverted the buffer and asked for a blur; that blur must
    // not turn around and commit the abandoned text.
    if (cancelRef.current) {
      cancelRef.current = false;
      setEditing(false);
      return;
    }
    if (!alignment) return;
    const parsed = parseStation(stationText);
    const internal = parsed === null ? null : displayToInternal(alignment, parsed);
    if (internal === null) {
      toast.error(t('alignment.invalidStation'));
      setStationText(formatStation(displayStation));
    } else {
      setStation(internal);
    }
    setEditing(false);
  };

  // Only live while a walk session is actually resolvable — `walk` can point at
  // a key that is no longer registered, in which case the toolbar renders null
  // and the callbacks would be no-ops that still swallow the keypress.
  const hk = { ignoreInputs: true, enabled: visible };
  // `[` / `]` are shared with the station-volumes dock, which re-binds them to
  // bin stepping (`useViewerHotkeys`). Both handlers move the same walk cursor,
  // so leaving ours live would double-step — and would step past the feature
  // gate the dock's bindings sit behind. The dock wins while it is open; every
  // other binding below stays exactly as it was.
  const bracketHk = { ...hk, enabled: hk.enabled && !stationDockOpen };
  useHotkeys([
    { hotkey: ']', callback: () => step(1), options: bracketHk },
    { hotkey: '[', callback: () => step(-1), options: bracketHk },
    // Punctuation keys aren't part of the library's modifier-prefixed string
    // union, so Shift+[ / Shift+] use the RawHotkey object form. Matching then
    // falls back to `event.code` via the library's PUNCTUATION_CODE_MAP, which
    // is a US physical-layout map — on layouts where BracketLeft/BracketRight
    // aren't the bracket keys these shortcuts land on whatever key sits there.
    { hotkey: { key: ']', shift: true }, callback: () => step(1, 10), options: bracketHk },
    { hotkey: { key: '[', shift: true }, callback: () => step(-1, 10), options: bracketHk },
    { hotkey: 'Home', callback: () => setStation(0), options: hk },
    { hotkey: 'End', callback: () => alignment && setStation(alignment.length), options: hk },
    { hotkey: 'Escape', callback: close, options: hk },
  ]);

  // `visible` is the rule; the two null checks after it are TypeScript's, not a
  // second rule — they narrow `walk` and `alignment` for everything below.
  if (!visible || !walk || !alignment) return null;
  const p = pointAtStation(alignment, walk.station);
  const grade = gradeAtStation(alignment, walk.station) * 100;
  const showCustomStep = customStepMode || !STEP_PRESETS.includes(walk.step);
  // Non-null only when the parser had to draw part of this alignment as a
  // straight chord; carries the whole list (corridor-trust T6c).
  const degraded = alignment.degradations.length > 0 ? degradedWarning(alignment, tp) : null;

  // Every square control in the bar — transport, mode toggles, close. Plain
  // <button>s (not FloatingToolbarButton) because they carry glyphs rather
  // than icons, and because the compact bar drops the portal tooltips: the
  // label is the native `title` (plus `aria-label`, which the glyph alone
  // does not give a screen reader). They still swallow click/pointerdown so a
  // press can't reach the canvas pickers — see FloatingToolbarButton.
  const iconButton = (
    testId: string,
    glyph: string,
    label: string,
    onClick: () => void,
    opts?: { active?: boolean; danger?: boolean },
  ) => (
    <button
      type='button'
      data-testid={testId}
      title={label}
      aria-label={label}
      aria-pressed={opts && 'active' in opts ? !!opts.active : undefined}
      className={cn(
        'h-8 w-8 shrink-0 rounded flex items-center justify-center text-sm transition-colors',
        opts?.danger
          ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30'
          : opts?.active
            ? 'bg-slate-800 text-white dark:bg-white dark:text-stone-700'
            : 'text-slate-700 hover:bg-slate-100 dark:text-stone-100 dark:hover:bg-stone-800',
      )}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}>
      {glyph}
    </button>
  );

  return (
    // Same top-centre slot as the other tool toolbars (add line / polygon /
    // surface, stockpile, profile hint) — the bottom edge already hosts the
    // main action toolbar, the tool buttons and the orientation gizmo.
    // One compact row: picker ⏮ ◀ [STA] ▶ ⏭ · step ◆ ⌖ ⬚ slider ✕.
    // Z and grade live in the station field's tooltip, not in the bar.
    //
    // The root deliberately drops `FloatingToolbar`'s own
    // `absolute left-1/2 -translate-x-1/2` centring and becomes a plain
    // `w-max max-w-full` flex item instead: it sits inside `Viewer`'s movable
    // `WalkToolbarWindow` (spec §12.2), next to that window's grip.
    // Self-centring in there would fight the window's own placement.
    //
    // `w-max` keeps the natural one-row width; `max-w-full` caps it at the
    // window, whose width is CSS shrink-to-fit — the content width, capped at
    // `canvas region width − the window's left offset`. When the window is
    // pushed against the region's right edge and that cap bites, `flex-wrap`
    // on the row breaks between the two `shrink-0` clusters below — two tidy
    // rows, never a ragged card.
    <FloatingToolbar
      className='relative top-0 left-auto z-auto w-max max-w-full translate-x-0'
      // `pl-5`, not `px-2`: the left 20 px of the card is where the window's
      // drag grip sits (see `WalkToolbarWindow`), inside the card, not beside it.
      rowClassName='flex-wrap items-center justify-center gap-1 py-1.5 pr-2 pl-5'>
      <div data-testid='alignment-walk-transport' className='flex shrink-0 items-center gap-1'>
        <Select value={walk.key} onValueChange={(key) => setWalk({ ...walk, key, station: 0 })}>
          <SelectTrigger
            className='h-8 max-w-[10rem] shrink-0 truncate px-2 text-xs'
            data-testid='alignment-select'
            title={alignment.name}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {alignmentGroups.map((g) => (
              <SelectGroup key={g.layerId}>
                <SelectLabel>{g.label}</SelectLabel>
                {g.items.map((a) => (
                  <SelectItem key={a.key} value={a.key}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        {degraded && (
          // One glyph, because the bar is a single row — the whole list rides
          // in the title/aria-label, the same text the run dialogs and the
          // properties drawer show.
          <span
            data-testid='alignment-degraded-warning'
            role='img'
            className='shrink-0 px-0.5 text-sm text-amber-600 dark:text-amber-400'
            title={degraded}
            aria-label={degraded}>
            ⚠
          </span>
        )}
        {iconButton('alignment-first', '⏮', t('alignment.first'), () => setStation(0))}
        {iconButton('alignment-prev', '◀', t('alignment.prev'), () => step(-1))}
        <Input
          data-testid='alignment-station-input'
          title={t('alignment.stationHint', { z: p.z.toFixed(2), grade: grade.toFixed(1) })}
          // 13ch of the mono stack — "STA 0+016.58" is 12 glyphs, the extra
          // one absorbs the border and the 2 px side padding.
          className='h-8 w-[13ch] shrink-0 px-0.5 text-center font-mono text-xs'
          value={stationText}
          onFocus={() => setEditing(true)}
          onChange={(e) => setStationText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitText();
            if (e.key === 'Escape') {
              cancelRef.current = true;
              setStationText(formatStation(displayStation));
              setEditing(false);
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
        {iconButton('alignment-next', '▶', t('alignment.next'), () => step(1))}
        {iconButton('alignment-last', '⏭', t('alignment.last'), () => setStation(alignment.length))}
      </div>
      <div data-testid='alignment-walk-modes' className='flex shrink-0 items-center gap-1'>
        <Select
          value={showCustomStep ? 'custom' : String(walk.step)}
          onValueChange={(v) => {
            if (v === 'custom') {
              setCustomStepMode(true);
              return;
            }
            setCustomStepMode(false);
            setStepDraft(null);
            setWalk({ ...walk, step: Number(v) });
          }}>
          <SelectTrigger
            className='h-8 w-[4.75rem] shrink-0 px-2 text-xs'
            data-testid='alignment-step'
            data-value={showCustomStep ? 'custom' : String(walk.step)}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STEP_PRESETS.map((s) => (
              <SelectItem key={s} value={String(s)}>
                {s} m
              </SelectItem>
            ))}
            <SelectItem value='custom'>{t('alignment.customStep')}</SelectItem>
          </SelectContent>
        </Select>
        {showCustomStep && (
          <span data-testid='alignment-custom-step' className='shrink-0'>
            <NumericField
              className='h-8 w-16 px-1 text-xs'
              value={stepDraft ?? walk.step}
              min='0.01'
              step='1'
              onChange={(v) => {
                if (!Number.isFinite(v)) return;
                // Typing a value that happens to equal a preset must not yank the
                // field out from under the cursor — latch the mode instead.
                setCustomStepMode(true);
                setStepDraft(v);
                // A transient 0 (typing "0.5") must not become the live step, but
                // it does have to stay in `stepDraft` so NumericField's resync
                // sees value === its own last onChange and leaves the buffer be.
                if (v > 0) setWalk({ ...walk, step: Math.max(0.01, v) });
              }}
            />
          </span>
        )}
        {iconButton(
          'alignment-snap',
          '◆',
          t('alignment.snapKeyPoints'),
          () => setWalk({ ...walk, snapKeyPoints: !walk.snapKeyPoints }),
          { active: walk.snapKeyPoints },
        )}
        {iconButton(
          'alignment-follow',
          '⌖',
          t('alignment.followCamera'),
          () => setWalk({ ...walk, follow: !walk.follow }),
          { active: walk.follow },
        )}
        {iconButton('alignment-clip', '⬚', t('alignment.clipFollow'), () => setClipFollow(!walk.clip), {
          active: !!walk.clip,
        })}
        {walk.clip && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type='button'
                data-testid='alignment-clip-size'
                title={t('alignment.clipSize')}
                aria-label={t('alignment.clipSize')}
                className='h-8 shrink-0 rounded px-1 flex items-center font-mono text-[10px] text-slate-700 hover:bg-slate-100 dark:text-stone-100 dark:hover:bg-stone-800 transition-colors'
                onPointerDown={(e) => e.stopPropagation()}>
                {clipDim(walk.clip.length)}×{clipDim(walk.clip.width)}×{clipDim(walk.clip.height)}
              </button>
            </PopoverTrigger>
            <PopoverContent className='w-48 p-3 flex flex-col gap-2' data-testid='alignment-clip-popover'>
              {(['length', 'width', 'height'] as const).map((k) => (
                <div key={k} className='flex items-center gap-2'>
                  <Label className='w-14 text-xs'>{t(`alignment.clip_${k}`)}</Label>
                  <NumericField
                    className='h-8 text-xs'
                    value={walk.clip![k]}
                    min='0.1'
                    step='1'
                    onChange={(v) => {
                      if (!Number.isFinite(v) || v <= 0) return;
                      // Clamp to 2 dp — a centimetre is all the slab needs.
                      setWalk({ ...walk, clip: { ...walk.clip!, [k]: Math.round(v * 100) / 100 } });
                    }}
                  />
                </div>
              ))}
            </PopoverContent>
          </Popover>
        )}
        <div className='w-24 shrink-0 px-1 flex items-center'>
          <Slider
            min={0}
            max={alignment.length}
            step={0.01}
            value={[walk.station]}
            onValueChange={([v]) => setStation(v)}
          />
        </div>
        {iconButton('alignment-close', '✕', t('alignment.close'), close, { danger: true })}
      </div>
    </FloatingToolbar>
  );
};
